import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Check, Sparkles, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionBuilder({ portal = "ORGANIZATION", accentColor = "from-emerald-500 to-teal-500" }) {
  const [plans, setPlans] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("PREMIUM");
  const [billing, setBilling] = useState("YEARLY");
  const [addons, setAddons] = useState({ storage_extra_gb: 0, integrations: 0, marketplace: false });
  const [quote, setQuote] = useState(null);
  const [contact, setContact] = useState({ company_name: "", contact_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/platform/public/plans`).then(({ data }) => {
      // Trial plans have their own signup flow (not Stripe checkout) — hide them from the paid builder.
      setPlans((data || []).filter((p) => !p.is_trial));
    });
    axios.get(`${BACKEND}/api/platform/public/roles?portal=${portal}`).then(({ data }) => setRoles(data));
  }, [portal]);

  // Live quote (no role config — plans are pre-configured)
  useEffect(() => {
    if (!plans.length) return;
    axios
      .post(`${BACKEND}/api/platform/public/quote`, {
        plan_code: selectedPlan,
        billing_cycle: billing,
        role_counts: {},
        addons,
      })
      .then(({ data }) => setQuote(data))
      .catch(() => {});
  }, [selectedPlan, billing, addons, plans.length]);

  const planObj = useMemo(() => plans.find((p) => p.code === selectedPlan), [plans, selectedPlan]);
  const planKey = selectedPlan.toLowerCase();

  // What's included: roles available in the chosen plan, with their min..max counts
  const includedRoles = useMemo(() => {
    return roles
      .filter((r) => (r[`max_in_${planKey}`] ?? 0) > 0)
      .map((r) => ({
        ...r,
        min: r[`min_in_${planKey}`] ?? 0,
        max: r[`max_in_${planKey}`] ?? 0,
      }));
  }, [roles, planKey]);

  const submit = async () => {
    if (!contact.company_name || !contact.email) {
      toast.error("Company and email are required.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${BACKEND}/api/platform/public/subscriptions/checkout`, {
        plan_code: selectedPlan,
        billing_cycle: billing,
        portal,
        addons,
        company_name: contact.company_name,
        contact_name: contact.contact_name,
        email: contact.email,
        phone: contact.phone,
        message: contact.message,
        origin_url: window.location.origin,
      });
      toast.success("Redirecting to secure Stripe checkout…");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden" data-testid="subscription-builder">
      {/* Step 1: Plan picker */}
      <div className="p-6 border-b border-white/10">
        <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Step 1 · Compare & pick a plan</div>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {plans.map((p) => (
            <button
              key={p.code}
              onClick={() => setSelectedPlan(p.code)}
              className={`relative text-left p-5 rounded-2xl border transition-all ${
                selectedPlan === p.code
                  ? "border-orange-400 bg-orange-500/10 shadow-lg shadow-orange-500/10"
                  : "border-white/10 bg-white/[0.02] hover:border-white/30"
              }`}
              data-testid={`plan-card-${p.code}`}
            >
              {p.highlight ? (
                <span className="absolute top-3 right-3 text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-orange-500 text-white">
                  POPULAR
                </span>
              ) : null}
              <div className="font-display text-2xl font-black text-white">{p.name}</div>
              <div className="text-xs text-white/50 mt-1">{p.tagline}</div>
              <div className="mt-4 text-3xl font-black text-white">
                ₹{(billing === "YEARLY" ? p.base_price_yearly : p.base_price_monthly).toLocaleString()}
                <span className="text-sm font-normal text-white/50">/{billing === "YEARLY" ? "yr" : "mo"}</span>
              </div>
              <div className="text-[11px] text-white/40 mt-1">{p.storage_gb} GB storage · up to {p.max_users_default} users</div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Switch checked={billing === "YEARLY"} onCheckedChange={(v) => setBilling(v ? "YEARLY" : "MONTHLY")} data-testid="billing-toggle" />
          <span className="text-sm text-white/70">Yearly billing (2 months free)</span>
        </div>
      </div>

      {/* Step 2: What's included */}
      <div className="p-6 border-b border-white/10">
        <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">
          Step 2 · What you get in {planObj?.name || "—"}
        </div>
        <div className="grid lg:grid-cols-2 gap-6 mt-5">
          {/* Features */}
          <div>
            <div className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-400" /> Features
            </div>
            <ul className="space-y-2">
              {(planObj?.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/80" data-testid={`included-feature-${i}`}>
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-white/60">
              <div className="p-2 rounded-lg border border-white/10 bg-black/30">
                <div className="text-[10px] uppercase tracking-wider text-white/40">Storage</div>
                <div className="text-white font-semibold mt-0.5">{planObj?.storage_gb} GB</div>
              </div>
              <div className="p-2 rounded-lg border border-white/10 bg-black/30">
                <div className="text-[10px] uppercase tracking-wider text-white/40">Trial</div>
                <div className="text-white font-semibold mt-0.5">{planObj?.trial_days} days</div>
              </div>
              <div className="p-2 rounded-lg border border-white/10 bg-black/30">
                <div className="text-[10px] uppercase tracking-wider text-white/40">Support</div>
                <div className="text-white font-semibold mt-0.5">{planObj?.support_level || "—"}</div>
              </div>
              <div className="p-2 rounded-lg border border-white/10 bg-black/30">
                <div className="text-[10px] uppercase tracking-wider text-white/40">Marketplace</div>
                <div className="text-white font-semibold mt-0.5">{planObj?.marketplace_access ? "Included" : "Add-on"}</div>
              </div>
            </div>
          </div>

          {/* Included roles */}
          <div>
            <div className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-400" /> Included roles
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {includedRoles.length === 0 ? (
                <div className="text-sm text-white/40">No roles configured for this portal yet.</div>
              ) : null}
              {includedRoles.map((r) => (
                <div
                  key={r.code}
                  className="flex items-center justify-between p-2.5 rounded-lg border border-white/10 bg-white/[0.02]"
                  data-testid={`included-role-${r.code}`}
                >
                  <div>
                    <div className="text-sm text-white">{r.name}</div>
                    <div className="text-[10px] font-mono text-white/40">{r.code}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Range</div>
                    <div className="text-sm font-bold text-white">{r.min}{r.min === r.max ? "" : `–${r.max}`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Add-ons */}
      <div className="p-6 border-b border-white/10">
        <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Step 3 · Add-ons (optional)</div>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.02]">
            <div>
              <div className="text-sm text-white font-semibold">Extra storage</div>
              <div className="text-[11px] text-white/40">₹5 / GB / mo</div>
            </div>
            <Input
              type="number"
              min={0}
              value={addons.storage_extra_gb}
              onChange={(e) => setAddons({ ...addons, storage_extra_gb: parseInt(e.target.value || "0") })}
              className="w-20 bg-transparent border-white/20 text-white"
              data-testid="addon-storage"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.02]">
            <div>
              <div className="text-sm text-white font-semibold">Integrations</div>
              <div className="text-[11px] text-white/40">₹999 / integration / mo</div>
            </div>
            <Input
              type="number"
              min={0}
              value={addons.integrations}
              onChange={(e) => setAddons({ ...addons, integrations: parseInt(e.target.value || "0") })}
              className="w-20 bg-transparent border-white/20 text-white"
              data-testid="addon-integrations"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.02]">
            <div>
              <div className="text-sm text-white font-semibold">Marketplace listing</div>
              <div className="text-[11px] text-white/40">₹1,499 / mo</div>
            </div>
            <Switch
              checked={!!addons.marketplace}
              onCheckedChange={(v) => setAddons({ ...addons, marketplace: v })}
              data-testid="addon-marketplace"
            />
          </div>
        </div>
      </div>

      {/* Step 4: Quote + contact */}
      <div className="p-6">
        <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-bold">Step 4 · Review & request demo</div>
        <div className="grid lg:grid-cols-2 gap-6 mt-4">
          <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-bold text-white">Your custom quote</span>
            </div>
            {!quote ? <div className="text-white/40 text-sm">Calculating…</div> : null}
            {quote ? (
              <div className="space-y-1.5">
                {quote.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-sm text-white/70" data-testid={`quote-line-${i}`}>
                    <span>{b.label}</span>
                    <span className={`font-mono ${b.amount < 0 ? "text-emerald-400" : "text-white"}`}>
                      ₹{b.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t border-white/10 my-3" />
                <motion.div
                  key={quote.total}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex justify-between items-center"
                  data-testid="quote-total"
                >
                  <span className="text-white/80 font-semibold">Total</span>
                  <span className={`font-display text-3xl font-black bg-gradient-to-r ${accentColor} bg-clip-text text-transparent`}>
                    ₹{quote.total.toLocaleString()}
                    <span className="text-base text-white/50 font-normal">/{billing === "YEARLY" ? "yr" : "mo"}</span>
                  </span>
                </motion.div>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-xs">Company</Label>
                <Input
                  value={contact.company_name}
                  onChange={(e) => setContact({ ...contact, company_name: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  data-testid="contact-company"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Your name</Label>
                <Input
                  value={contact.contact_name}
                  onChange={(e) => setContact({ ...contact, contact_name: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Email</Label>
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Phone</Label>
                <Input
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  className="bg-transparent border-white/20 text-white"
                  data-testid="contact-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/70 text-xs">Anything we should know?</Label>
              <Textarea
                rows={3}
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="bg-transparent border-white/20 text-white"
                data-testid="contact-message"
              />
            </div>
            <Button
              onClick={submit}
              disabled={submitting}
              size="lg"
              className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white font-bold rounded-xl"
              data-testid="submit-lead"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Pay & subscribe — Stripe secure checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
